/**
 * Parse the filename out of an HTTP Content-Disposition header value.
 *
 * Prefers the RFC 5987 `filename*=UTF-8''<percent-encoded>` form when
 * present (correctly carries non-ASCII filenames). Falls back to the
 * legacy quoted `filename="..."` form for ASCII-only names.
 *
 * The previous implementation used `/filename="?([^"]+)"?/i`, which
 * silently captured the tail of an RFC 2047 encoded-word when the
 * backend stuffed a non-latin-1 filename into a quoted-string and
 * Django MIME-encoded the whole header value. That produced visible
 * garbage like `3D=22K4_..._=22?=` in the file viewer title bar.
 */
export function parseContentDispositionFilename(
  header: string | null | undefined
): string | null {
  if (!header) return null;

  // Defensive: an entire header wrapped in an RFC 2047 encoded-word
  // (`=?charset?Q?...?=`) is the legacy-bug shape. Browsers don't
  // decode it in Content-Disposition, so any name we'd pull out
  // would be quoted-printable garbage. Bail rather than surface it.
  if (/^=\?[^?]+\?[QqBb]\?.*\?=\s*$/.test(header.trim())) {
    return null;
  }

  const starMatch = header.match(/filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i);
  if (starMatch) {
    const charset = (starMatch[1] || "utf-8").toLowerCase();
    const encoded = starMatch[2].trim();
    if (charset === "utf-8" || charset === "us-ascii" || charset === "") {
      try {
        return decodeURIComponent(encoded);
      } catch {
        // Malformed percent-encoding — fall through to the legacy form.
      }
    }
  }

  const quotedMatch = header.match(/filename\s*=\s*"((?:\\.|[^"\\])*)"/i);
  if (quotedMatch) {
    return quotedMatch[1].replace(/\\(.)/g, "$1");
  }

  const bareMatch = header.match(/filename\s*=\s*([^;]+)/i);
  if (bareMatch) {
    return bareMatch[1].trim();
  }

  return null;
}
